<template>
  <b-nav-item-dropdown
    class="dropdown-notification mr-25"
    menu-class="dropdown-menu-media"
    right
  >
    <template #button-content>
      <feather-icon
        :badge="unreadNotificationsCount"
        badge-classes="bg-danger"
        class="text-body"
        icon="BellIcon"
        size="21"
      />
    </template>

    <!-- Header -->
    <li class="dropdown-menu-header">
      <div class="dropdown-header d-flex">
        <h4 class="notification-title mb-0 mr-auto">Notifications</h4>
        <b-badge
          v-if="unreadNotificationsCount > 0"
          pill
          variant="light-primary"
        >
          {{ unreadNotificationsCount }} New
        </b-badge>
      </div>
    </li>

    <!-- Notifications -->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="scrollable-container media-list scroll-area"
      tagname="li"
    >
      <!-- Account Notification -->
      <b-link
        v-for="notification in notifications"
        :key="notification.key"
        @click="clickedOnNotification(notification)"
      >
        <b-media :class="{ 'bg-light-primary': !notification.opened }">
          <template #aside>
            <b-avatar
              size="32"
              :src="notification.sender_details.profile_image_url"
              :variant="'primary'"
            />
          </template>
          <p class="media-heading">
            <span class="font-weight-bolder">
              {{ notification.content.title }}
            </span>
          </p>
          <!-- add a download button when download is finished -->
          <!-- Change the conditional requirement to check the notification.type instead of the message -->
          <!-- v-if="notification.content.message === 'compiling'" -->
          <div
            v-if="notification.type === 'ongoing_download_notification'"
            class="d-flex justify-content-center notification-text m-1"
          >
            <!-- <b-spinner /> -->
            <!-- <p>Progress: {{ notification.content.message }}%</p> -->
            <b-progress
              :id="`notificationDropdownDownloadProgress`"
              class="block w-100"
              :variant="variant"
              :value="Number(notification.content.message)"
            />
          </div>
          <!-- v-else-if="notification.content.message === 'compile finished'" -->
          <button
            v-else-if="notification.type === 'finished_download_notification'"
            class="btn btn-primary w-75 mt-1"
            :disabled="
              new Date(notification.download_link_expired_date) <=
              new Date(new Date().toUTCString().substring(0, 25))
            "
            @click="
              handleDownloadFileButtonClick(
                notification.content.link,
                notification.title.split(' ')[2]
              )
            "
          >
            {{
              new Date(notification.download_link_expired_date) <=
              new Date(new Date().toUTCString().substring(0, 25))
                ? "Download Link Expired"
                : "Download File"
            }}
          </button>
          <div
            v-else-if="
              notification.type ===
              'application_modification_request_notification'
            "
            class="d-flex flex-column justify-content-between notification-text"
          >
            <p class="">
              {{ notification.content.message }}
            </p>
            <div class="d-flex">
              <button
                class="btn btn-primary w-75"
                @click="
                  acceptModifiedApplicationNotification(
                    notification.meta.data,
                    notification.meta.application_id,
                    notification.id,
                    notification.meta.related_notif_id,
                    notification.meta.job_position,
                    notification.meta.request_sender
                  )
                "
              >
                Accept
              </button>

              <button
                class="btn btn-danger w-75 ml-2"
                @click="
                  updateModifiedApplicationNotificationStatus(
                    `rejected_notification`,
                    notification.id,
                    notification.meta.related_notif_id,
                    notification.meta.job_position,
                    notification.meta.request_sender
                  )
                "
              >
                Reject
              </button>
            </div>
          </div>
          <small v-else class="notification-text">{{
            notification.content.message
          }}</small>
        </b-media>
      </b-link>

      <div
        class="view-all-notifications d-flex justify-content-center py-1 cursor-pointer"
        @click="goToNotificationsPage"
      >
        <h5 class="m-0 p-0 font-weight-bolder">View All Notifications</h5>
      </div>

      <!-- System Notification Toggler -->
      <div class="media d-flex align-items-center">
        <h6 class="font-weight-bolder mr-auto mb-0">System Notifications</h6>
        <b-form-checkbox :checked="true" switch />
      </div>

      <!-- System Notifications -->
      <b-link
        v-for="notification in systemNotifications"
        :key="notification.subtitle"
      >
        <b-media>
          <template #aside>
            <b-avatar size="32" :variant="notification.type">
              <feather-icon :icon="notification.icon" />
            </b-avatar>
          </template>
          <p class="media-heading">
            <span class="font-weight-bolder">
              {{ notification.title }}
            </span>
          </p>
          <small class="notification-text">{{ notification.subtitle }}</small>
        </b-media>
      </b-link>
    </vue-perfect-scrollbar>

    <!-- Cart Footer -->
    <li class="dropdown-menu-footer">
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        block
        :disabled="unreadNotificationsCount === 0"
        @click="readAllNotifications"
      >
        Read all notifications
      </b-button>
    </li>
  </b-nav-item-dropdown>
</template>

<script>
import { notificationsMixins } from "@/views/notifications/mixins/notificationsMixins"
import {
  BNavItemDropdown,
  BBadge,
  BMedia,
  BLink,
  BAvatar,
  BButton,
  BFormCheckbox,
} from "bootstrap-vue"
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import Ripple from "vue-ripple-directive"
import { v4 as uuid } from "uuid"

export default {
  components: {
    BNavItemDropdown,
    BBadge,
    BMedia,
    BLink,
    BAvatar,
    VuePerfectScrollbar,
    BButton,
    BFormCheckbox,
  },
  directives: {
    Ripple,
  },

  mixins: [notificationsMixins],
  created() {
    // init
    // this.$http.get("/api/user").then((response) => {
    //   this.currentUserId = response.data.id
    //   const websocketBaseUrl = process.env.VUE_APP_REST_API_WEBSOCKET
    //   this.loadingProgressWebsocket = new WebSocket(
    //     `${websocketBaseUrl}/api/notifications/websocket/${this.currentUserId}`
    //   )
    //   this.loadingProgressWebsocket.onopen = () => {
    //     this.loadingProgressWebsocket.onmessage = (event) => {
    //       // fetch all notifications again
    //       this.getNotifications()
    //     }
    //   }
    // })
  },
  mounted() {
    this.$socket.client.on("update_notifications", () => {
      const now = new Date()
      if (now - this.lastNotificationUpdate >= 500) {
        this.getNotifications()
        this.lastNotificationUpdate = now
      }
    })

    this.$socket.client.on("finished_compiling_csv", (data) => {

      const receiverId = data.receiver_id

      if (receiverId === this.$store.state.auth.ActiveUser.id) {
        const s3FileName = data.s3_file_name
        const tableName = data.table_name
        const notificationId = data.notification_id

        const filename = `${tableName}-${Date.now()}`

        const updatedData = {
          title: `Preparing Download File`,
          content: {
            title: `Preparing Download File`,
            message: `0`,
            link: "",
          },
          type: "ongoing_download_notification",
        }

        this.$http
          .put(`/api/notifications/${notificationId}`, {
            data: updatedData,
          })
          .then((updateDownloadNotification) => {
            this.$socket.client.emit("refresh_notifications")

            this.$generateDownloadUrl(
              s3FileName,
              "nil",
              "notification",
              filename
            ).then((generateDownloadURLResponse) => {
              const fileDownloadLink = generateDownloadURLResponse

              const readyToDownloadNotifUpdate = {
                title: `File is Ready to be Downloaded`,
                content: {
                  title: `File is Ready to be Downloaded`,
                  message: ``,
                  link: fileDownloadLink.download_url,
                },
                download_link_expired_date: fileDownloadLink.expired_date,
                type: "finished_download_notification",
              }

              setTimeout(() => {
                this.$http
                  .put(`/api/notifications/${notificationId}`, {
                    data: readyToDownloadNotifUpdate,
                  })
                  .then((updateNotificationResponse) => {
                    this.$socket.client.emit("refresh_notifications")
                  })
              }, 400)
            })
          })
      }
    })
  },
  data() {
    return {
      systemNotifications: [],

      perfectScrollbarSettings: {
        maxScrollbarLength: 60,
        wheelPropagation: false,
      },
      loadingProgressWebsocket: null,
      currentUserId: "",
      lastNotificationUpdate: 0,
    }
  },
  methods: {
    async handleDownloadFileButtonClick(downloadURL, fileName) {
      const link = document.createElement("a")
      link.href = downloadURL
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables/variables.scss";

.view-all-notifications {
  &:hover,
  &:hover h5 {
    background: $primary;
    color: white;
  }
}

.notification-item:hover {
  // box-shadow:inset 0 0 0 99999px rgba(255,255,255,0.2);
  background: $primary;
}
</style>
